/*
 * Developed for G.J. Gardner Homes by Softeq Development Corporation
 * http://www.softeq.com
 */

import { Component, OnInit } from '@angular/core';
import { ActivatedRoute } from '@angular/router';
import {
  AbstractFormComponent,
  CommonActions,
  FormActionPipelines,
  FormFlowState,
  whenFormInvalid,
} from '../../../skeletons';
import { BuilderNavigationService } from '../../../services';
import {
  ActionFlow,
  AppRouter,
  FormStateSnapshot,
  getRouteInitData,
  streamValueAction,
} from '@gh/common-shared';
import { ID } from '@gh/common-shared-data';
import { translationTuple } from '@gh/core-mls';
import {
  DialogService, noAnswer, noAsDefaultAnswer, NotificationService, WaitingService, yesAnswer, yesNoCancelConfirmation, yesNoConfirmation, } from '@gh/core-ui';
import { isNotNil, openInNewWindow } from '@gh/core-util';
import { merge$, of$ } from '@gh/rx';
import { map$, filter$, switchMap$, tap$ } from '@gh/rx/operators';
import { Observable } from 'rxjs';
import {
  getJobDrawsInvoiceId,
  JobDrawDetailsTabBusinessService,
  JobDrawsDetails,
  JobDrawsDetailsInfo,
  JobDrawsDetailsInvoice,
  JobDrawsDetailsTab,
} from '../../resources/job-details-tab';
import { JobDetailsModel } from '../../services/jobt-details-model.service';
import { JobDrawDetailsDrawsFormState } from './job-details-draws-form.service';
import { JobFinancialsDrawsFilterCode } from '../../resources/job-financials.model';

@Component({
  standalone: false,
  selector: 'gh-job-details-draws-tab',
  templateUrl: 'job-details-draws-tab.component.html',
  providers: [JobDrawDetailsDrawsFormState],
})
export class JobDetailsDrawsTabComponent extends AbstractFormComponent<JobDrawsDetails> implements OnInit {
  private reportUrl: string;
  private jobId: ID;

  constructor(private route: ActivatedRoute,
              private router: AppRouter,
              private model: JobDetailsModel,
              private navigations: BuilderNavigationService,
              private dialogs: DialogService,
              private notifications: NotificationService,
              private pipelines: FormActionPipelines,
              private commonActions: CommonActions,
              public form: JobDrawDetailsDrawsFormState,
              private waitings: WaitingService,
              private business: JobDrawDetailsTabBusinessService) {
    super();
  }

  get completeFlow(): ActionFlow<FormFlowState<JobDrawsDetails>> {
    return this.pipelines.complete({
      validate: this.commonActions.validateWithEffect(whenFormInvalid, () => this.handleErrors()),
      execute: streamValueAction((state: FormFlowState<JobDrawsDetails>) =>
        // check if contract price should be updated
        this.isContractPriceShouldBeUpdated().pipe(
          // save changes associated with draw rule
          switchMap$((isContractUpdated) => this.business.recalculateDraws(
            this.jobId,
            this.form.prepareRecalculateRequest(true, isContractUpdated),
            this.form.modifiedRuleId).pipe(this.waitings.operator())),
          map$((value) => ({ ...state, value })))),
      finish: ({ value }) => {
        this.form.build(value);
        this.form.resetDirty();
      },
    });
  }

  get recalculateFlow(): ActionFlow<FormFlowState<JobDrawsDetails>> {
    return this.pipelines.recalculate({
      validate: this.commonActions.validateWithEffect(whenFormInvalid, () => this.handleErrors()),
      execute: this.commonActions.execute(() => this.business.recalculateDraws(
        this.jobId,
        this.form.prepareRecalculateRequest(false),
        this.form.modifiedRuleId)),
      finish: ({ value }) => {
        this.form.mergeOnRecalculate(value);
        this.form.setDirty();
      },
    });
  }

  ngOnInit(): void {
    const { jobId, draws, reportUrl } = getRouteInitData(this.route);
    this.jobId = jobId;
    this.reportUrl = reportUrl;
    this.form.build(draws);
  }

  setInfo(info: FormStateSnapshot<JobDrawsDetailsInfo>): void {
    this.form.setInfo(info);
  }

  setCurrentRuleId(ruleId: ID): void {
    const originalRuleId = this.form.currentRuleId;

    this.form.markDrawRuleAsModified();
    this.form.setCurrentRuleId(ruleId);

    if (this.form.hasInvoice) {
      const confirmation$ = this.dialogs.confirmation(yesNoConfirmation('msg_job_change_rule'));

      confirmation$.pipe(
        filter$(yesAnswer))
        .subscribe(() => {
          this.form.finalizeCurrentRuleChange();
          this.recalculate();
        });

      confirmation$.pipe(
        filter$(noAsDefaultAnswer))
        .subscribe(() => {
          // rollback current rule change
          this.form.setCurrentRuleId(originalRuleId);
        });
    } else {
      this.form.finalizeCurrentRuleChange();
      this.recalculate();
    }
  }

  onInvoiceSelected(invoice: JobDrawsDetailsInvoice | undefined): void {
    this.form.setSelectedInvoiceId(invoice ? getJobDrawsInvoiceId(invoice) : undefined);
  }

  onSelectedTabChange(tab: JobDrawsDetailsTab): void {
    this.form.selectTab(tab);
  }

  onOpenDraw(invoice: JobDrawsDetailsInvoice): void {
    this.confirmNavigation().subscribe(() => {
      if (isNotNil(invoice.variationId) && invoice.isCreatedFromChangeOrder) {
        this.router.navigateToChild(['/job-admin/jobs', this.jobId, 'change-orders', invoice.variationId, 'invoice'], {
          parentTitle: translationTuple('lbl_job_number_with_param', { number: String(this.model.jobNumber) }),
        });
      } else {
        this.router.navigateToChild(['/accounting/jobs', this.jobId, 'draws', invoice.invoiceId], {
          parentTitle: translationTuple('lbl_job_number_with_param', { number: String(this.model.jobNumber) }),
        });
      }
    });
  }

  onOpenInXero(invoice: JobDrawsDetailsInvoice): void {
    this.confirmNavigation().subscribe(() => {
      openInNewWindow(invoice.xeroUrl);
    });
  }

  onRecalculate(): void {
    const flow = this.recalculateFlow;

    flow.run({
      form: this.form,
      value: this.form.submitValue,
    });
  }

  onPrintSchedule(): void {
    openInNewWindow(this.reportUrl);
  }

  onRefreshSchedule(): void {
    // check if form is valid
    if (this.form.invalid) {
      this.handleErrors();
      return;
    }

    this.form.markScheduleAsRefreshed();
    this.business.refreshDraws(this.jobId, this.form.modifiedRuleId!, this.form.prepareRecalculateRequest(false)).pipe(
      this.waitings.operator())
      .subscribe((draws) => {
        this.form.mergeOnRecalculate(draws);
        this.form.setDirty();
      });
  }

  goToAel(): void {
    this.navigations.goToAel({
      entityId: this.jobId,
      entityType: 'JOB',
      label: translationTuple('lbl_job_number_with_param', { number: String(this.model.jobNumber) }),
    });
  }

  onDrawsFilterChange(code: JobFinancialsDrawsFilterCode): void {
    // this.drawListFilter = code;
    // this.jobFinancialsDrawListHighlights = this.getOriginalJobFinancialsDrawList()
    //   .filter(({ status }: JobFinancialsDraw) => {
    //     if (code === JobFinancialsDrawsFilterCode.isAllDraws) {
    //       return includes([InvoiceStatus.Paid], status);
    //     }
    //   });
    // this.jobFinancialsDrawList = this.getOriginalJobFinancialsDrawList()
    //   .filter(({ status }: JobFinancialsDraw) => {
    //     if (code === JobFinancialsDrawsFilterCode.isOnlyActiveDraws) {
    //       return includes([InvoiceStatus.Authorized, InvoiceStatus.Paid], status);
    //     } else if (code === JobFinancialsDrawsFilterCode.isAllDraws) {
    //       return true;
    //     }
    //   });
  }

  private recalculate(): void {
    const flow = this.recalculateFlow;

    flow.run({
      form: this.form,
      value: this.form.submitValue,
    }).toFinalState().pipe(
      map$(({ success }) => Boolean(success)))
      .subscribe((success) => {
        if (!success) {
          this.form.setRecalculationNeeded();
        }
      });
  }

  private confirmNavigation(): Observable<boolean> {
    if (this.form.dirty) {
      const confirmation$ = this.dialogs.confirmation(yesNoCancelConfirmation('msg_unsaved_info'));

      const yes$ = confirmation$.pipe(
        filter$(yesAnswer),
        switchMap$(() => this.completeFlow.run({
          form: this.form,
          value: this.form.submitValue,
          trigger: 'process',
        }).toFinalState()),
        map$((state) => state.success),
        filter$(Boolean),
        map$(() => true));

      const no$ = confirmation$.pipe(
        filter$(noAnswer),
        map$(() => true),
        tap$(() => this.form.reset()));

      return merge$(yes$, no$);
    } else {
      return of$(true);
    }
  }

  private handleErrors(): void {
    this.form.highlightMasterInfo();
  }

  private isContractPriceShouldBeUpdated(): Observable<boolean> {
    return this.form.isPriceWasModified
      ? this.dialogs.confirmation(yesNoConfirmation('msg_update_job_estimate')).pipe(map$(yesAnswer))
      : of$(false);
  }
}
